/**
 * Site header.
 *
 * @author Simon Lagerlöf <contact@smn.codes>
 * @copyright 2024 Simon Lagerlöf
 * @since 1.0.0
 */

export default class SiteHeader {
  private header: HTMLElement | null;
  private subMenus: NodeListOf<HTMLElement>;

  constructor() {
    this.refresh();
  }

  public refresh(): void {
    this.header = document.querySelector('header.site-header');
    this.subMenus = document.querySelectorAll(
      'ul.site-header-nav__submenu',
    );

    this.mobileMenuInteractions();
    this.subMenuInteractions();
  }

  private mobileMenuInteractions(): void {
    if (this.header instanceof HTMLElement) {
      new MobileMenu(this.header);
    }
  }

  private subMenuInteractions(): void {
    for (const subMenu of this.subMenus) {
      new SubMenu(subMenu);
    }
  }
}

class MobileMenu {
  private toggleButton: HTMLButtonElement;
  private menu: HTMLElement;
  private nav: HTMLElement;
  private page: HTMLElement;

  private className = 'open';

  private isOpen = false;

  constructor(private header: HTMLElement) {
    const toggleButton = this.header.querySelector(
      'button.site-header-menu-toggle',
    );
    if (!(toggleButton instanceof HTMLButtonElement)) {
      return;
    }
    this.toggleButton = toggleButton;

    const menu = this.header.querySelector('div.site-header-menu');
    if (!(menu instanceof HTMLElement)) {
      return;
    }
    this.menu = menu;

    const nav = this.header.querySelector('nav.site-header-nav');
    if (!(nav instanceof HTMLElement)) {
      return;
    }
    this.nav = nav;

    const page = document.body.querySelector('main.page');
    if (!(page instanceof HTMLElement)) {
      return;
    }
    this.page = page;

    this.toggleButton?.addEventListener('click', () => {
      {
        if (!this.isOpen) {
          this.enable();
        } else {
          this.disable();
        }
      }
    });

    for (const anchor of this.header.querySelectorAll('a')) {
      anchor.addEventListener('click', () => this.disable());
    }
  }

  private enable(): void {
    this.isOpen = true;

    this.toggleClassLists();
    this.toggleAttributes();
    this.toggleHeight();
    this.toggleButtonLabel();
    this.checkMenuOverflow();
    this.toggleZIndex();

    window.addEventListener('click', (event) => {
      const target = event.target as Node;

      if (
        !this.menu?.contains(target) &&
        !this.toggleButton?.contains(target)
      ) {
        this.disable();
      }
    });
  }

  private disable(): void {
    this.isOpen = false;

    this.toggleZIndex();
    this.toggleClassLists();
    this.toggleAttributes();
    this.toggleHeight();
    this.toggleButtonLabel();
    this.checkMenuOverflow();
  }

  private toggleAttributes(): void {
    this.toggleButton.setAttribute(
      'aria-expanded',
      this.isOpen ? 'true' : 'false',
    );
  }

  private toggleClassLists(): void {
    const action = this.isOpen ? 'add' : 'remove';

    this.toggleButton.classList[action](this.className);
    this.menu.classList[action](this.className);
    this.page.classList[action](`menu-${this.className}`);
    document.body.classList[action]('disable-scroll');
  }

  private toggleButtonLabel(): void {
    const attribute = this.toggleButton.getAttribute('data-labels');

    const data = attribute ? JSON.parse(attribute) : null;

    this.toggleButton.title = this.isOpen ? data.close : data.open;
  }

  private toggleHeight(): void {
    this.menu.style.height = this.isOpen
      ? `${this.nav.offsetHeight}px`
      : '';
  }

  private toggleZIndex(): void {
    if (window.innerWidth > 960) {
      return;
    }

    this.header.style.zIndex = this.isOpen ? '2' : '1';
  }

  private checkMenuOverflow(): void {
    const windowHeight = window.innerHeight;
    const menuHeight = this.menu.clientHeight;

    if (menuHeight / windowHeight > 0.9) {
      this.nav.style.overflowY = 'scroll';
    }
  }
}

class SubMenu {
  private parent: HTMLElement;
  private toggleButton: HTMLButtonElement;

  private isOpen: boolean = false;

  private className = 'open';

  constructor(private element: Element) {
    if (!(this.element.parentElement instanceof HTMLElement)) {
      return;
    }

    this.parent = this.element.parentElement;

    const toggleButton = this.parent.querySelector(
      'button.site-header-nav__submenu-toggle',
    );

    if (!(toggleButton instanceof HTMLButtonElement)) {
      return;
    }

    this.toggleButton = toggleButton;

    this.parent.addEventListener('mouseover', () => this.open());
    this.parent.addEventListener('mouseout', () => this.close());
    this.toggleButton.addEventListener('click', () => this.toggle());

    this.parent.parentElement
      ?.querySelectorAll(
        'a:is(.site-header-nav__link,.site-header-nav__sublink)',
      )
      .forEach((button) =>
        button.addEventListener('click', () => this.close()),
      );
  }

  private toggle(): void {
    if (!this.isOpen) {
      this.open();
    } else {
      this.close();
    }
  }

  private open(): void {
    this.isOpen = true;

    this.toggleClassLists();
    this.setAttributes();
  }

  private close(): void {
    this.isOpen = false;

    this.toggleClassLists();
    this.setAttributes();
  }

  private toggleClassLists(): void {
    this.element.classList[this.isOpen ? 'add' : 'remove'](
      this.className,
    );
    this.toggleButton.classList[this.isOpen ? 'add' : 'remove'](
      this.className,
    );
  }

  private setAttributes(): void {
    this.parent.setAttribute(
      'aria-expanded',
      this.isOpen ? 'true' : 'false',
    );
  }
}
